import type { SignInProvider } from "./client.ts"

/*
 * What the sign-in card says about the ways in, and how it reads what someone types into it.
 */

const ways: Record<SignInProvider, string> = {
  google: "your company Google account",
  slack: "your account in the company's Slack",
  "email-code": "a code emailed to you",
  password: "your password",
}

/** The line under the card's buttons, naming every way in the stage offers. */
export function howToSignIn(offered: readonly SignInProvider[]) {
  const named = (Object.keys(ways) as SignInProvider[])
    .filter((way) => offered.includes(way))
    .map((way) => ways[way])
  const listed =
    named.length < 2
      ? (named[0] ?? ways.google)
      : named.length === 2
        ? `${named[0]}, or ${named[1]}`
        : `${named.slice(0, -1).join(", ")}, or ${named.at(-1)}`
  return `Use ${listed}.`
}

/** A sign-in code as typed or pasted, spaces and dashes taken out: six digits, or none. */
export function codeTyped(text: string) {
  const digits = text.replace(/[\s-]/g, "")
  return /^\d{6}$/.test(digits) ? digits : undefined
}

/** Whether what was typed is shaped like an email address, worth asking a code for. */
export const isAddress = (text: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim())

/** Whether what was typed could be a username or an email, worth signing in or resetting with. */
export const isSignInName = (text: string) => /^[^\s]{3,}$/.test(text.trim())

/**
 * Why a password wasn't set, in words: the rules Beacon holds every password to (12 to 128
 * characters, none known from a breach), the current one wrong, or a check that couldn't run.
 */
export function describePasswordRefusal(refusal: string | undefined) {
  switch (refusal) {
    case "password-too-short":
      return "A password needs at least 12 characters."
    case "password-too-long":
      return "A password can have at most 128 characters."
    case "password-breached":
      return "That password is in a known data breach, so it isn't safe anywhere. Choose another."
    case "password-check-unavailable":
      return "Beacon couldn't check that password just now. Try again in a minute."
    case "wrong-password":
      return "Your current password isn't right."
    default:
      return "Beacon couldn't set that password. Try again."
  }
}
